import React, { useEffect } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const faqs = [
  {
    q: '¿Qué es GeoVis?',
    a: 'GeoVis es un visor GIS web profesional para evaluar la viabilidad territorial de proyectos de energía renovable y, en general, cualquier análisis de suelo. Unifica decenas de capas oficiales (catastro, urbanismo, ambientales, infraestructuras, regulatorias) sobre un mismo mapa interactivo y genera informes PDF profesionales en menos de 24 horas.'
  },
  {
    q: '¿Qué fuentes de datos y capas incluye?',
    a: 'Más de 20 fuentes oficiales y cerca de 50 módulos de capas. En España: Catastro, IGN, SIGPAC, SIOSE, planeamiento urbanístico, Red Natura 2000, vías pecuarias, montes de utilidad pública, red eléctrica, ferrocarriles, carreteras, hidrografía, zonas inundables, servidumbres aeronáuticas, patrimonio cultural, protección costera, zonificación de ruido, posidonia y muchas más. El informe PDF cruza 88 capas territoriales con tu proyecto.'
  },
  {
    q: '¿En qué países está disponible?',
    a: 'España es la cobertura principal (península, Canarias, Baleares, Ceuta y Melilla). Además ya operamos con capas oficiales propias en Portugal, Chile y Perú, e Italia está en fase inicial. Añadimos nuevos países de forma continua.'
  },
  {
    q: '¿Cuánto cuesta GeoVis?',
    a: 'Hay un plan Free (catastro, SIGPAC y búsquedas básicas, sin tarjeta). El plan Pro cuesta 49 €/mes (539 €/año) e incluye acceso completo a todas las capas, herramientas e isócronas. El plan Premium cuesta 99 €/mes (1.089 €/año) y añade un informe PDF gratis cada mes, tarifa reducida en informes adicionales, prioridad y SLA. Hay plan Enterprise a medida para equipos grandes o on-premise.'
  },
  {
    q: '¿Cómo funciona el informe PDF?',
    a: 'El informe se paga por proyecto: 99 € de base que cubren hasta 10 hectáreas, más una tarifa por hectárea adicional (2 €/ha estándar, 1 €/ha en Premium), con un tope máximo de 999 € por informe. Cualquier usuario, incluso sin suscripción, puede comprar un informe puntual. Los usuarios Premium reciben uno gratis cada mes (hasta 100 ha).'
  },
  {
    q: '¿Necesito conocimientos técnicos o de GIS?',
    a: 'No. GeoVis está diseñado para usarse como un visor moderno (estilo Google Earth) sin formación GIS previa, manteniendo capacidades profesionales de cruce de capas, exportación y generación de informes. Cualquier técnico del equipo puede usarlo desde el primer día.'
  },
  {
    q: '¿Cómo se actualizan los datos?',
    a: 'Las capas se sincronizan automáticamente con sus fuentes oficiales. Trabajas siempre con la información más reciente publicada por los organismos competentes, sin tener que descargar ni actualizar nada manualmente.'
  },
  {
    q: '¿Puedo probarlo antes de pagar?',
    a: 'Sí. El plan Free permite usar GeoVis sin tarjeta de crédito (catastro, SIGPAC y búsquedas). Los planes Pro y Premium incluyen 7 días de prueba gratuita. Además, puedes comprar un informe PDF puntual desde 99 € sin crear cuenta.'
  }
];

// Por defecto, las preguntas del visor territorial; la home de Urbanismo pasa las suyas.
export const FAQ = ({ items = faqs, titulo = 'Preguntas frecuentes', subtitulo = 'Lo esencial, sin ruido.' }) => {
  // Schema FAQPage para GEO (ChatGPT, Perplexity, Claude, Gemini)
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    };

    // Añadir o actualizar el script JSON-LD
    let scriptTag = document.querySelector('script[data-faq-schema]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-faq-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(faqSchema);

    return () => {
      // Limpiar al desmontar
      const script = document.querySelector('script[data-faq-schema]');
      if (script) script.remove();
    };
  }, [items]);

  return (
    <section id="faq" className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
      <div data-reveal className="fade-in-on-scroll">
        <h2 className="text-3xl sm:text-4xl">
          {titulo}
        </h2>
        <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl">{subtitulo}</p>
      </div>

      <div className="mt-10 max-w-3xl">
        <Accordion data-testid="faq-accordion" type="single" collapsible className="space-y-3">
          {items.map((faq, i) => (
            <AccordionItem
              key={faq.q}
              value={`item-${i}`}
              data-reveal
              className={`bg-card rounded-lg px-6 border border-border fade-in-on-scroll ${
                ['reveal-delay-0', 'reveal-delay-1', 'reveal-delay-2', 'reveal-delay-3'][i % 4]
              }`}
            >
              <AccordionTrigger className="text-left text-foreground/80 hover:text-foreground hover:no-underline py-5 text-base font-medium">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent forceMount className="text-muted-foreground leading-relaxed pb-5 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQ;
